import { evaluateShowIf, type ShowIfCondition, type ShowIfEntryMap } from "@shared/gate-eval";

/**
 * s223 (D-223.18) — WHICH FIELDS A ROW DOES NOT ASK, resolved once.
 *
 * A field's `showIf` is evaluated against the row it sits on: a charity beneficiary is not asked "Under 18?"
 * or a date of birth, a child with no guardianship gate is not asked who the guardian is. Completion has
 * resolved this per row since s156 and the planner lens reads the same answer; the document/form read path
 * did not, which is the hole D-223.18 part 3 names — a value the screen covers must not reach a court form.
 *
 * 🔴 The rule this encodes is "if it is not visible, it does not exist" — a READ rule, not a delete. The
 * value stays stored and comes back the moment the row asks the question again (D-223.18 part 2).
 *
 * Pure. `evaluateShowIf` remains the SINGLE evaluator (never an inline condition) and `showIf` targets a
 * sibling by `fieldId`, which is why the caller passes both the fields and the row's entries by id.
 */

export type RowGateField = { fieldKey: string; fieldId: string; conditions: ShowIfCondition | null };
export type RowGateEntry = { fieldId?: string | null; value?: string | null };

/** The field keys this row does NOT ask, by their own `showIf`. Pure; empty when nothing is gated. */
export function hiddenFieldKeysOnRow(
  fields: readonly RowGateField[],
  entries: readonly RowGateEntry[],
): string[] {
  const gated = fields.filter((f) => f.conditions);
  if (gated.length === 0) return [];
  const entryMap: ShowIfEntryMap = {};
  for (const e of entries) if (e.fieldId) entryMap[e.fieldId] = { value: e.value ?? "" };
  return gated.filter((f) => !evaluateShowIf(f.conditions, entryMap)).map((f) => f.fieldKey);
}

/**
 * The row's values with every field it does not ask removed — what a READER sees (D-223.18 part 3).
 * Pure; the stored entries are untouched.
 */
export function askedValuesOnRow(
  fields: readonly RowGateField[],
  entries: readonly RowGateEntry[],
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  const hidden = new Set(hiddenFieldKeysOnRow(fields, entries));
  if (hidden.size === 0) return { ...values };
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) if (!hidden.has(k)) out[k] = v;
  return out;
}
